/*
|--------------------------------------------------------------------------
| Imports
|--------------------------------------------------------------------------
*/

import { PerspectiveCamera, Scene, Vector2, WebGLRenderer } from 'three';
import { CanvasGet } from '~/Canvas';
import { CameraGet } from '~/Camera';
import { SceneGet } from '~/Scene';
import { RenderOptions } from './Types';

/*
|--------------------------------------------------------------------------
| Locals
|--------------------------------------------------------------------------
*/

let _autoUpdate: boolean = true;
let _scene: Scene = null;
let _camera: PerspectiveCamera = null;
let _canvas: HTMLCanvasElement = null;
let _renderer: WebGLRenderer = null;
let _maxAnisotropy: number = null;
let _resizeTimeout: NodeJS.Timeout = null;
let _resizeTo: Vector2 = null;

/*
|--------------------------------------------------------------------------
| Private Functions
|--------------------------------------------------------------------------
*/

/**
 * @return {void}
 */
function _resize(): void
{
    _renderer.setSize(_resizeTo?.x ?? _canvas.clientWidth, _resizeTo?.y ?? _canvas.clientHeight, false);
    _resizeTimeout = null;
    _resizeTo = null;
}

/**
 * @return {void}
 */
function _startResizeDebounce(): void
{
    if (_resizeTimeout) {
        clearTimeout(_resizeTimeout);
    }

    _resizeTimeout = setTimeout(_resize, 250);
}

/*
|--------------------------------------------------------------------------
| Public Functions
|--------------------------------------------------------------------------
*/

/**
 * @return {void}
 */
export function RenderStart(): void
{
    _autoUpdate = true;
    RenderUpdate();
}

/**
 * @return {void}
 */
export function RenderStop(): void
{
    _autoUpdate = false;
}

/**
 * @return {void}
 */
export function RenderUpdate(): void
{
    _renderer.render(_scene, _camera);

    if (_canvas.width !== _canvas.clientWidth || _canvas.height !== _canvas.clientHeight) {
        if (_resizeTo?.x !== _canvas.clientWidth || _resizeTo?.y !== _canvas.clientHeight) {
            if (_resizeTo) {
                _resizeTo.x = _canvas.clientWidth;
                _resizeTo.y = _canvas.clientHeight;
            } else {
                _resizeTo = new Vector2(_canvas.clientWidth, _canvas.clientHeight);
            }

            _startResizeDebounce();
        }
    }

    if (_autoUpdate) {
        requestAnimationFrame(RenderUpdate);
    }
}

/**
 * @return {number}
 */
export function RenderGetMaxAnisotropy(): number
{
    return _maxAnisotropy;
}

/**
 * @return {number}
 */
export function RenderInit(options?: RenderOptions): void
{
    _scene = SceneGet();
    _camera = CameraGet();
    _canvas = CanvasGet();
    _renderer = new WebGLRenderer({ canvas: _canvas, antialias: options?.antialias ?? false });
    _maxAnisotropy = _renderer.capabilities.getMaxAnisotropy();
    _resize();
}
